import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { fromOrder, fromSubscription, useAppDispatch } from '../../../../store';
import {
  Feature,
  PlanInterval,
  SecondarySource,
  SubscriptionSource,
} from '../../../../models';
import { SelectQuota } from './SelectQuota';
import { BonusSeatTreatment } from './BonusSeatTreatment';
import { getQuotaFromPathParam } from '../../../../utils/utils';

const useStyles = makeStyles(theme => ({
  seatsLabel: {
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: 1,
    color: '#808B9A',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(1),
  },
  seatsNote: {
    fontWeight: 400,
    fontSize: 13,
    color: '#808B9A',
    marginLeft: theme.spacing(2),
  },
  seatsNoteLink: {
    color: '#5959EB',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: 500,
  },
  annualTotalAmount: {
    fontWeight: 800,
    fontSize: 18,
    color: '#5959EB',
  },
}));

const MAX_SEATS = process.env.REACT_APP_TRUE_TEAM_MAX_SEATS;

export const TrueSeatSelect = (
  { productId, billingInterval }: {
    productId: string;
    billingInterval: PlanInterval;
  },
) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const selectedSeats = getQuotaFromPathParam(Feature.TRUE_TEAM);
  const selectRecurlyTrueSubscriptions = useMemo(
    () => fromSubscription.selectTrueSubscriptionList(SubscriptionSource.RECURLY),
    [],
  );
  const trueSubscriptions = useSelector(
    selectRecurlyTrueSubscriptions,
  );
  const subscription = trueSubscriptions.find(
    sub => sub.feature === Feature.TRUE_TEAM
      || sub.feature === Feature.TRUE_ENTERPRISE,
  );
  const selfServeSubscription = trueSubscriptions.find(
    sub => (
      sub.feature === Feature.TRUE_TEAM
      || sub.feature === Feature.TRUE_ENTERPRISE
    ) && sub.secondarySource !== SecondarySource.DIRECT_SALE,
  );
  const [seats, setSeats] = useState(
    selectedSeats ? Number(selectedSeats) : (subscription ? 0 : 1),
  );

  useEffect(() => {
    if (selectedSeats && seats < selectedSeats) {
      setSeats(selectedSeats);
    }
  }, [selectedSeats]);

  const delayDebounceFn = useMemo(
    () => debounce((nextSeats: number, interval: PlanInterval) => {
      dispatch(fromOrder.setTruePlatformPreviewRequest({
        productCode: `${productId}_${PlanInterval.getPlanIntervalCode(interval)}`,
        quantity: nextSeats,
        ...(selfServeSubscription?.recurlySubUuId
          ? { recurlySubscriptionUuid: selfServeSubscription.recurlySubUuId }
          : {}),
      }));
    }, 1000),
    [dispatch, productId, selfServeSubscription?.recurlySubUuId],
  );

  useEffect(() => {
    delayDebounceFn(seats, billingInterval);
  }, [seats, billingInterval, delayDebounceFn]);

  useEffect(() => () => {
    delayDebounceFn.cancel();
  }, [delayDebounceFn]);

  return (
    <Box
      id="seat-select"
      py={2}
    >
      <Typography className={classes.seatsLabel}>
        <FormattedMessage id="page.plan.v2.seats.label" />
      </Typography>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <SelectQuota
          productId={productId}
          initQuota={subscription?.quota ? 0 : 1}
          quota={seats}
          setQuota={setSeats}
          maxQuota={MAX_SEATS ? Number(MAX_SEATS) : undefined}
          width={40}
          height={40}
        />
        {MAX_SEATS && (
          <Box>
            <Typography fontWeight={600} fontSize={13} color="#22283c">
              <FormattedMessage id="page.plan.v2.seats.max" values={{ max: MAX_SEATS }} />
            </Typography>
            <Typography fontSize={13} color="#808B9A">
              <FormattedMessage
                id="page.plan.v2.seats.need.more"
                values={{
                  link: (chunks: string) => (
                    <Box
                      component="a"
                      href="mailto:success@katalon.com"
                      className={classes.seatsNoteLink}
                    >
                      {chunks}
                    </Box>
                  ),
                }}
              />
            </Typography>
          </Box>
        )}
        <BonusSeatTreatment
          seats={seats}
          isAnnual={billingInterval === PlanInterval.YEAR}
          hasSubscription={subscription !== undefined}
          setSeats={setSeats}
        />
      </Box>
    </Box>
  );
};
